import React, { useRef, useEffect, useState } from 'react';
import axios from 'axios';
import Modal from './Modal';
import Form from '../Form/Form';
import InputQuantity from '../Form/Fields/InputQuantity';
import Textarea from '../Form/Fields/Textarea';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowDown,
  faArrowUp,
  faCheck,
  faPlus,
  faTriangleExclamation,
} from '@fortawesome/free-solid-svg-icons';
import { API_BASE_URL } from '../../api';

const INBOUND  = 'INBOUND';

const config = {
  INBOUND: {
    title:      'Entrée de stock',
    icon:       faArrowDown,
    iconBg:     '#ecfdf5',
    iconColor:  '#059669',
    accentBg:   '#f0fdf4',
    accentBorder:'#bbf7d0',
    accentColor:'#15803d',
    accentText: 'Les unités seront ajoutées au lot',
    btnClass:   'btn-success',
    btnLabel:   'Confirmer l\'entrée',
    toastMsg:   'Stock ajouté avec succès',
  },
  OUTBOUND: {
    title:      'Sortie de stock',
    icon:       faArrowUp,
    iconBg:     '#fef2f2',
    iconColor:  '#dc2626',
    accentBg:   '#fff5f5',
    accentBorder:'#fecaca',
    accentColor:'#b91c1c',
    accentText: 'Les unités seront retirées du lot',
    btnClass:   'btn-danger',
    btnLabel:   'Confirmer la sortie',
    toastMsg:   'Stock retiré avec succès',
  },
};

const StockEditModal = ({ batchId, orderableId, action, show, onClose, onSuccess }) => {
  const modalRef = useRef();
  const cfg = config[action] ?? config.INBOUND;
  const [outOfStockSold, setOutOfStockSold] = useState(0);
  // the optional motif stays behind a link — most movements don't need one
  const [showDescription, setShowDescription] = useState(false);
  useEffect(() => { if (show) setShowDescription(false); }, [show]);

  // Only relevant for an INBOUND entry — warns if units of this product were already sold
  // "hors stock" since the last stock entry, so the pharmacist doesn't enter the full received
  // quantity and overstate the stock.
  useEffect(() => {
    if (!show || action !== INBOUND || !orderableId) {
      setOutOfStockSold(0);
      return;
    }
    axios.get(`${API_BASE_URL}/api/v1/stock/orderable/${orderableId}`, { withCredentials: true })
      .then(({ data }) => setOutOfStockSold(data.outOfStockSoldSinceLastEntry ?? 0))
      .catch(() => setOutOfStockSold(0));
  }, [show, action, orderableId]);

  const handleSubmit = (data) => {
    axios.post(`${API_BASE_URL}/api/v1/stock/movement`, {
      batchId,
      quantity:     parseInt(data.quantity, 10),
      movementType: action,
      description:  data.description,
    }, { withCredentials: true })
      .then(() => {
        toast.success(cfg.toastMsg);
        modalRef.current?.hide();
        onSuccess?.();
      })
      .catch((err) => {
        toast.error('Une erreur est survenue');
        console.error(err);
      });
  };

  return (
    <Modal
      ref={modalRef}
      id="createMovementModal"
      classSize="modal-sm modal-dialog-centered"
      dialogStyle={{ maxWidth: 340 }}
      title={cfg.title}
      show={show}
      onClose={onClose}
      icon={cfg.icon}
      iconBg={cfg.iconBg}
      iconColor={cfg.iconColor}
      footer={
        <button
          className={`btn ${cfg.btnClass} w-100 fw-bold`}
          type="submit"
          form="stockMovementForm"
          style={{ borderRadius: 10, padding: '10px 0', fontSize: 14 }}
        >
          <FontAwesomeIcon icon={faCheck} className="me-2" />
          {cfg.btnLabel}
        </button>
      }
    >
      <Form
        onSubmit={handleSubmit}
        defaultValues={{ quantity: 1, description: '' }}
        id="stockMovementForm"
      >
        {/* Action banner */}
        <div style={{
          background: cfg.accentBg,
          border: `1px solid ${cfg.accentBorder}`,
          borderRadius: 10,
          padding: '10px 14px',
          marginBottom: 20,
          display: 'flex',
          alignItems: 'center',
          gap: 10,
        }}>
          <div style={{
            width: 30, height: 30, borderRadius: 8, flexShrink: 0,
            background: cfg.iconBg, color: cfg.iconColor,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 13,
          }}>
            <FontAwesomeIcon icon={cfg.icon} />
          </div>
          <span style={{ fontSize: 13, fontWeight: 500, color: cfg.accentColor }}>
            {cfg.accentText}
          </span>
        </div>

        {outOfStockSold > 0 && (
          <div style={{
            display: 'flex', alignItems: 'flex-start', gap: 8,
            marginBottom: 20, padding: '10px 12px',
            background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8,
            fontSize: 12.5, color: '#b91c1c', lineHeight: 1.4,
          }}>
            <FontAwesomeIcon icon={faTriangleExclamation} style={{ marginTop: 2, flexShrink: 0 }} />
            <span>
              Attention : {outOfStockSold} unité{outOfStockSold > 1 ? 's' : ''} de ce produit {outOfStockSold > 1 ? 'ont' : 'a'} été
              vendue{outOfStockSold > 1 ? 's' : ''} hors stock avant cette entrée.
            </span>
          </div>
        )}

        {/* Quantity */}
        <div style={{ marginBottom: 16 }}>
          <label style={{
            display: 'block', fontSize: 12, fontWeight: 600,
            color: '#6b7280', textTransform: 'uppercase',
            letterSpacing: '0.05em', marginBottom: 8,
          }}>
            Quantité
          </label>
          <InputQuantity
            name="quantity"
            validationRules={{
              required: 'La quantité est requise',
              min: { value: 1, message: 'Minimum 1' },
            }}
          />
        </div>

        {/* Description — hidden until asked for */}
        {showDescription ? (
          <div>
            <label style={{
              display: 'block', fontSize: 12, fontWeight: 600,
              color: '#6b7280', textTransform: 'uppercase',
              letterSpacing: '0.05em', marginBottom: 8,
            }}>
              Motif / Description <span style={{ fontWeight: 400, color: '#9ca3af', textTransform: 'none', letterSpacing: 0 }}>— optionnel</span>
            </label>
            <Textarea
              name="description"
              placeholder="Ex : réapprovisionnement fournisseur, retour client..."
            />
          </div>
        ) : (
          // light gray strip, the link centred in it
          <div style={{
            display: 'flex', justifyContent: 'center',
            padding: '10px 12px', borderRadius: 8, background: '#f8fafc',
          }}>
            <button
              type="button"
              onClick={() => setShowDescription(true)}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: 0, border: 'none', background: 'none',
                fontSize: 12.5, fontWeight: 500, color: '#9ca3af', cursor: 'pointer',
              }}
              onMouseEnter={e => { e.currentTarget.style.color = '#6b7280'; }}
              onMouseLeave={e => { e.currentTarget.style.color = '#9ca3af'; }}
            >
              <FontAwesomeIcon icon={faPlus} style={{ fontSize: 11 }} />
              Ajouter un motif
            </button>
          </div>
        )}
      </Form>
    </Modal>
  );
};

export default StockEditModal;
